import { ChangeDetectionStrategy, Component } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, refCount, publishReplay } from 'rxjs/operators';
import { Settings, UrlBuilder } from '../../core';
import { CategoryNaturalKey, CategoriesStorage, Category } from '../../documents';
import { SponsorDocumentsCounter } from '../../documents/models/documents-counter';
import { NavigationLink } from '../../navigation';
import { SponsorStudy } from '../../sponsor-dashboard/sponsor-study';
import { SponsorRouteData } from '../sponsor-route-data';
import { SponsorUnreadReportsStorage } from '../sponsor-unread-reports-storage';
import { UserPermissions } from '../../user-permissions/user-permissions';
import { SponsorEQueriesCountersStorage } from '../e-queries/sponsor-e-queries-counters-storage';
import { chain } from 'lodash';
import { SampleTrackerData } from '../../sample-tracking/group-info/study-group-info';
import { SampleTrackingOptionResolver } from '../../layout/sample-tracking/sample-tracking-option.resolver';
import { UserStorage } from '../../user';
import { UserPermissionsStorage } from '../../user-permissions/user-permissions-storage';

@Component({
    selector: 'sponsor-nav',
    template: require('./sponsor-navigation.component.html'),
    styles: [require('./sponsor-navigation.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SponsorNavigationComponent {
    public topLevelMenu: Observable<NavigationLink[]>;

    private selectedStudies: Observable<SponsorStudy>;
    private readonly resultAlertsLinkClassName: CategoryNaturalKey = 'result-alerts';
    private readonly sponsorCommunicationLinkClassName: CategoryNaturalKey = 'sponsor-communications';

    constructor(
        private readonly urlBuilder: UrlBuilder,
        private readonly routeData: SponsorRouteData,
        private readonly unreadReportsRegistry: SponsorUnreadReportsStorage,
        private readonly categoryStorage: CategoriesStorage,
        private readonly settings: Settings,
        private readonly sponsorEQueriesCountersStorage: SponsorEQueriesCountersStorage,
        private readonly userPermissionsStorage: UserPermissionsStorage,
        private readonly sampleTrackingOptionResolver: SampleTrackingOptionResolver,
        private readonly userStorage: UserStorage
    ) {
        this.selectedStudies = this.routeData.selectedStudy;
        this.topLevelMenu = this.getLinks()
            .pipe(
                publishReplay(1),
                refCount()
            );
    }

    private getSponsorCommunicationsLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .sponsorCommunications()
                        .url()
                ));
    }

    private getStudySubjectsLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .subjects(null)
                        .url()
                ));
    }

    private getStudyDocumentsLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .studyDocuments()
                        .url()
                ));
    }

    private getLabReportsLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .labReports()
                        .url()
                ));
    }

    private getSiteDocumentsLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .siteDocuments()
                        .url()
                ));
    }

    private getSiteOverviewLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .sites()
                        .url()
                ));
    }

    private getLabDataLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .trendAnalysis()
                        .url()
                ));
    }

    private getLabResultsLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .testResults()
                        .url()
                ));
    }

    private getActivityReportLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .activityReports()
                        .url()
                ));
    }

    private getResultAlertsLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .resultAlerts()
                        .url()
                ));
    }

    private getKitInventoryLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .kitInventory()
                        .url()
                ));
    }

    private getTableauLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .tableau()
                        .url()
                ));
    }

    private getEQueriesLink() {
        return this.selectedStudies
            .pipe(
                map(study =>
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .eQueries()
                        .url()
                ));
    }

    private getSampleTrackingLink() {
        let sampleTrackerData: SampleTrackerData = new SampleTrackerData();
        this.routeData.selectedStudy.subscribe(study => {
            sampleTrackerData.protocolFk = study.protocolId;
            sampleTrackerData.protocolCode = study.protocolName;
            sampleTrackerData.sponsorName = study.sponsorName;
        });
        localStorage.setItem('dashboardData', JSON.stringify(sampleTrackerData));
        return this.urlBuilder
            .esa()
            .sampletracking()
            .protocolInfo(sampleTrackerData)
            .url();
    }

    private getLinks(): Observable<NavigationLink[]> {
        return combineLatest(
            this.routeData.selectedStudy,
            this.routeData.studyPermissions,
            this.categoryStorage.activityReportCategories,
            this.categoryStorage.studyDocumentsCategories,
            this.categoryStorage.siteDocumentsCategories,
            this.userPermissionsStorage.userPermissions
        ).pipe(
            map(([study, userPermissions, activityReportCategories, studyDocumentsCategories, siteDocumentsCategories, appPermissions]) => {

                const alertsCounter = this.getSpecificCounter(study, 'result-alerts');
                const alertsUnreadCount$ = this.getLinkBadgetText(alertsCounter);
                const alertsIsDisabled$ = this.getLinkDisabledStatus(alertsCounter);
                const eQueriesCounter$ = this.getEQueriesCounter(study);
                const eQueriesDisabled$ = this.getEQueriesLinkDisabledStatus(study);

                const commCounter = this.getSpecificCounter(study, 'sponsor-communications');
                const commUnreadCount$ = this.getLinkBadgetText(commCounter);
                const commIsDisabled$ = this.getLinkDisabledStatus(commCounter);

                const navigationLinks: NavigationLink[] = [];

                navigationLinks.push(new NavigationLink('Sites', this.getSiteOverviewLink(), 'business', null, null, of(study.numberOfSites === 0), ''));
                navigationLinks.push(new NavigationLink('Result Alerts', this.getResultAlertsLink(), 'warning', alertsUnreadCount$, null, alertsIsDisabled$, this.resultAlertsLinkClassName));
                navigationLinks.push(new NavigationLink('Lab Reports', this.getLabReportsLink(), 'assignment', null, null, of(false), '', true));
                if (userPermissions.canViewCategory('sponsor-communications')) {
                    navigationLinks.push(new NavigationLink('Communications', this.getSponsorCommunicationsLink(), 'announcement', commUnreadCount$, null, commIsDisabled$, this.sponsorCommunicationLinkClassName));
                }
                if (studyDocumentsCategories.some(c => userPermissions.canViewCategory(c.categoryId))) {
                    navigationLinks.push(new NavigationLink('Study Documents', this.getStudyDocumentsLink(), 'import_contacts', null, null, of(false), ''));
                }

                navigationLinks.push(new NavigationLink('Study Subjects', this.getStudySubjectsLink(), 'people', null, null, of(study.numberOfSubjects === 0), ''));

                if (siteDocumentsCategories.some(c => userPermissions.canViewCategory(c.categoryId))) {
                    navigationLinks.push(new NavigationLink('Site Documents', this.getSiteDocumentsLink(), 'folder', null, null, of(study.numberOfSites === 0), ''));
                }

                navigationLinks.push(new NavigationLink('Test Results', this.getLabResultsLink(), 'grid_on', null, null, of(false), ''));
                navigationLinks.push(new NavigationLink('Trend Analysis', this.getLabDataLink(), 'multiline_chart', null, null, of(false), ''));
                if (userPermissions.has('KitInventoryandExpirationReport')) {
                    navigationLinks.push(new NavigationLink('Kit Inventory', this.getKitInventoryLink(), 'card_travel', null, null, of(false), '', true));
                }

                if (this.settings.enableStudyHealthDashboard && appPermissions.has('ViewStudyHealthDashboard')) {
                    navigationLinks.push(new NavigationLink('Study Oversight', this.getTableauLink(), 'view_quilt', null, null, of(false), '', true));
                }

                this.appendReportsLink(navigationLinks, activityReportCategories, userPermissions);

                if (userPermissions.has('EQueryViewAndRespond')) {
                    navigationLinks.push(new NavigationLink('eQueries', this.getEQueriesLink(), 'settings_input_antenna', eQueriesCounter$, null, eQueriesDisabled$, 'e-queries'));
                }

                if (this.sampleTrackingOptionResolver.doesPermissionExists(this.userStorage.currentUser)) {
                    navigationLinks.push(new NavigationLink('Sample Tracking', of(this.getSampleTrackingLink()), null, null, null, of(false), '', true, 'images/sample_tracking/search-test-tube.svg'));
                }

                return navigationLinks;
            }));
    }

    private getSpecificCounter(study: SponsorStudy, categoryId: CategoryNaturalKey): Observable<SponsorDocumentsCounter> {
        return this.unreadReportsRegistry
            .getFreshUnreadsStream()
            .pipe(
                filter(x => x.length > 0),
                map(counters => counters.find(counter => counter.rootCategoryId === categoryId && counter.protocolId === study.protocolId))
            );
    }

    private getLinkBadgetText(counter$: Observable<SponsorDocumentsCounter>): Observable<string> {
        return counter$.pipe(
            map(counter => counter && counter.totalUnread ? counter.totalUnread.toString(10) : '')
        );
    }

    private getLinkDisabledStatus(counter$: Observable<SponsorDocumentsCounter>): Observable<boolean> {
        return counter$.pipe(
            map(counter => !counter || counter.totalDocs === 0)
        );
    }

    private appendReportsLink(navigationLinks: NavigationLink[], activityReportCategories: Category[], userPermissions: UserPermissions) {
        const hasAccessToProtocols = userPermissions.has('ViewActivityReportProtocolAccess');
        const otherPermissions = hasAccessToProtocols
            ? true
            : activityReportCategories.some(c => userPermissions.canViewActivityReport(c.categoryId));

        if (hasAccessToProtocols || otherPermissions) {
            navigationLinks.push(new NavigationLink('Reports', this.getActivityReportLink(), 'streetview', null, null, of(false), ''));
        }
    }

    private getEQueriesCounter(study: SponsorStudy) {
        return this.sponsorEQueriesCountersStorage.counters.pipe(
            map(counters => {
                const counter = chain(counters)
                    .filter(c => c.protocolId === study.protocolId)
                    .sumBy(c => c.totalUnresolved.size)
                    .value();
                return counter
                    ? counter.toString(10)
                    : '';
            }),
        );
    }

    private getEQueriesLinkDisabledStatus(study: SponsorStudy) {
        return this.sponsorEQueriesCountersStorage.counters.pipe(
            map(counters => {
                const total = chain(counters)
                    .filter(c => c.protocolId === study.protocolId)
                    .sumBy(c => c.total)
                    .value();
                return total === 0;
            }),
        );
    }
}
